import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {Store, select} from '@ngrx/store';
import {AddressDto} from '@sportngin/se-commerce-sdk';
import {Observable, takeUntil} from 'rxjs';
import {SeFeToastService} from 'se-fe-toast';
import {NavigationEventService} from '../../../add-ons/navigation-event.service';
import {BreakpointUtilityService} from '../../../common/service';
import * as cartActions from '../../../common/shopping-cart/cart.actions';
import {selectCheckEmptyCart} from '../../../common/shopping-cart/cart.selectors';
import {AddressApiService} from '../../../data/api/service';
import {AddressFormGroup, CartItem, ControlsOf} from '../../../data/model';
import {OnDestroySubject} from '../../../data/model/on-destroy-subject';
import * as addressActions from '../../address.actions';
import * as addressSelector from '../../address.selectors';
import {
  AddressValidator,
  zipCodeRegex,
} from '../../validators/address-validator';
import {countries, formFieldsConfig, states} from './shipping-form.constants';

@Component({
  selector: 'app-shipping-form',
  templateUrl: './shipping-form.component.html',
  styleUrls: ['./shipping-form.component.scss'],
  host: {class: 'app-shipping-form'},
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ShippingFormComponent extends OnDestroySubject implements OnInit {
  @Output() sendTheForm = new EventEmitter<FormGroup>();
  @ViewChild('formScrollAnchor')
  formScrollAnchor?: ElementRef<HTMLElement>;
  suggestedAddress$: Observable<AddressDto | null>;
  cartItems$: Observable<CartItem[]>;
  continueClicked = false;
  shippingForm: FormGroup<ControlsOf<AddressFormGroup>>;
  routedFlow = true;
  ready = false;
  //Form Fields
  formFieldsConfig = formFieldsConfig;
  // Drop Downs
  states = states;
  countries = countries;
  private smallScreen = false;

  constructor(
    protected breakpointUtils: BreakpointUtilityService,
    private cdr: ChangeDetectorRef,
    private addressApiService: AddressApiService,
    private store: Store,
    private continueEvent: NavigationEventService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: SeFeToastService,
  ) {
    super();
    this.suggestedAddress$ = this.store.select(
      addressSelector.selectSelectedAddress,
    );
    this.checkEmptyCartAndCompleteOrder();
    this.routedFlow = true;
  }

  get originalAddress(): AddressFormGroup {
    return this.shippingForm.value as AddressFormGroup;
  }

  ngOnInit(): void {
    this.createForm();
    this.store
      .select(addressSelector.selectOriginalAddress)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(address => {
        if (address) {
          this.shippingForm.patchValue(address);
          this.cdr.markForCheck();
        }
      });
    this.breakpointUtils.smallScreen$
      .pipe(takeUntil(this.onDestroy))
      .subscribe(isSmall => {
        this.smallScreen = isSmall;
      });
    this.subscribeToFormChanges();
    this.continueEvent.continueClick
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.continueClicked = true;
        this.continue();
      });
  }

  checkEmptyCartAndCompleteOrder(): void {
    this.store
      .pipe(takeUntil(this.onDestroy), select(selectCheckEmptyCart))
      .subscribe(emptyCart => {
        if (emptyCart) {
          this.ready = false;
          this.cdr.markForCheck();
          this.completeOrder();
        }
      });
  }

  subscribeToFormChanges(): void {
    this.shippingForm.statusChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(status => {
        if (status === 'INVALID' && this.shippingForm.errors) {
          this.shippingForm.markAllAsTouched();
        }
        this.cdr.markForCheck();
      });

    // Subscribe to all controls status changes to trigger change detection when errors are set
    Object.values(this.shippingForm.controls).forEach(control => {
      control.statusChanges.pipe(takeUntil(this.onDestroy)).subscribe(() => {
        this.cdr.markForCheck();
      });
    });

    this.shippingForm.controls.state.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        const zipErrors = this.shippingForm.controls.zip.errors;
        if (zipErrors) {
          const {required, pattern, ...addressErrors} = zipErrors;
          if (Object.keys(addressErrors).length > 0) {
            const newErrors = required || pattern ? {required, pattern} : null;
            this.shippingForm.controls.zip.setErrors(newErrors);
          }
        }

        const stateErrors = this.shippingForm.controls.state.errors;
        if (stateErrors) {
          const {required, ...addressErrors} = stateErrors;
          if (Object.keys(addressErrors).length > 0) {
            const newErrors = required ? {required} : null;
            this.shippingForm.controls.state.setErrors(newErrors);
          }
        }
        this.clearAddressErrorsOnUserInput();
        this.cdr.markForCheck();
      });

    this.shippingForm.controls.zip.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(value => {
        if (value) {
          const numbers = value.replace(/\D/g, '');
          if (numbers.length > 5) {
            const formatted = `${numbers.slice(0, 5)}-${numbers.slice(5, 9)}`;
            if (value !== formatted) {
              this.shippingForm.controls.zip.setValue(formatted, {
                emitEvent: false,
              });
            }
          }
        }
        const zipErrors = this.shippingForm.controls.zip.errors;
        if (zipErrors) {
          const {required, pattern, ...addressErrors} = zipErrors;
          if (Object.keys(addressErrors).length > 0) {
            const newErrors = required || pattern ? {required, pattern} : null;
            this.shippingForm.controls.zip.setErrors(newErrors);
          }
        }
        this.clearAddressErrorsOnUserInput();
        this.cdr.markForCheck();
      });

    // Subscribe to address field changes to clear errors when user starts typing
    const addressFields = [
      this.shippingForm.controls.address1,
      this.shippingForm.controls.address2,
      this.shippingForm.controls.country,
    ];

    addressFields.forEach(control => {
      control.valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(() => {
        this.clearAddressErrorsOnUserInput();
      });
    });

    // Handle city changes separately to clear city control errors
    this.shippingForm.controls.city.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        const cityErrors = this.shippingForm.controls.city.errors;
        if (cityErrors) {
          const {required, ...addressErrors} = cityErrors;
          if (Object.keys(addressErrors).length > 0) {
            const newErrors = required ? {required} : null;
            this.shippingForm.controls.city.setErrors(newErrors);
          }
        }
        this.clearAddressErrorsOnUserInput();
        this.cdr.markForCheck();
      });
  }

  continue(): void {
    if (this.shippingForm.status === 'PENDING') {
      return;
    }
    this.prepareFormForValidation();
    if (!this.shippingForm.valid) {
      this.notifyValidationIssue();
      return;
    }

    this.shippingForm.markAsPending();
    this.validateAddress();
  }

  public markFormGroupTouchedAndDirty(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      if (control instanceof FormGroup) {
        this.markFormGroupTouchedAndDirty(control);
      } else {
        control.markAsTouched({onlySelf: true});
        control.markAsDirty({onlySelf: true});
      }
    });
  }

  public prepareFormForValidation(): void {
    this.trimFormValues();
    this.shippingForm.markAllAsTouched();
    Object.values(this.shippingForm.controls).forEach(control => {
      control.markAsDirty();
      control.updateValueAndValidity();
    });
    this.shippingForm.setErrors(null);
    this.continueClicked = true;
    this.cdr.markForCheck();
  }

  public trimFormValues(): void {
    Object.values(this.shippingForm.controls).forEach(control => {
      if (typeof control.value === 'string') {
        const trimmedValue = control.value.trim();
        if (control.value !== trimmedValue) {
          control.setValue(trimmedValue);
          control.markAsDirty();
          control.markAsTouched();
        }
      }
    });
  }

  public validateAddress(): void {
    this.shippingForm.markAsPending();
    (
      AddressValidator.validateAddress(
        this.addressApiService,
        this.store,
      )(this.shippingForm) as Observable<ValidationErrors | null>
    )
      .pipe(takeUntil(this.onDestroy))
      .subscribe(errors => this.handleValidationResult(errors));
  }

  protected completeOrder(): void {
    this.store.dispatch(cartActions.completeOrder());
  }

  protected createForm(): void {
    this.shippingForm = new FormGroup<ControlsOf<AddressFormGroup>>({
      firstName: new FormControl('', {
        validators: [Validators.required],
        nonNullable: true,
      }),
      lastName: new FormControl('', {
        validators: [Validators.required],
        nonNullable: true,
      }),
      address1: new FormControl('', [Validators.required]),
      address2: new FormControl(''),
      country: new FormControl('', [Validators.required]),
      city: new FormControl('', [Validators.required]),
      state: new FormControl('', [Validators.required]),
      zip: new FormControl(null, [
        Validators.required,
        Validators.pattern(zipCodeRegex),
      ]),
    });
    this.ready = true;
  }

  /**
   * Returns an error message for the zip code form control based on validation errors.
   * This handles the zip code validation since we only use the VerifyAddress endpoint
   *
   * @returns {string | null} The error message if validation errors exist, null otherwise:
   *  - 'Invalid ZIP code. Suggested: {suggestedZip}' when zip code is invalid with a suggestion
   *  - A supported-length message when the ZIP code format is incorrect
   *  - 'ZIP code is required' when zip code is empty
   *  - null when there are no validation errors
   */
  // eslint-disable-next-line @typescript-eslint/member-ordering
  public getZipErrorMessage(): string | null {
    const zipControl = this.shippingForm.get('zip');
    let errorMessage: string | null = null;

    if (zipControl?.errors) {
      // Check for specific error types in order of priority
      if (zipControl.errors['invalidZip']) {
        errorMessage = `Invalid ZIP code. Suggested: ${zipControl.errors['suggestedZip']}`;
      } else if (zipControl.errors['pattern']) {
        errorMessage = 'Please enter a valid 4, 5, or 9-digit ZIP code';
      } else if (zipControl.errors['required']) {
        errorMessage = 'ZIP code is required';
      }
    }

    return errorMessage;
  }

  /**
   * Checks if the form has address validation errors.
   * Address validation errors include: invalidAddress, invalidZip, zipStateMismatch, incompleteAddress,
   * invalidState, and invalidCity (checked at both form and control level)
   *
   * @returns {boolean} True if there are address validation errors, false otherwise
   */
  // eslint-disable-next-line @typescript-eslint/member-ordering
  public get hasAddressError(): boolean {
    if (!this.shippingForm) {
      return false;
    }

    // Check form-level errors
    if (this.shippingForm.errors) {
      const errors = this.shippingForm.errors;
      if (
        errors['invalidAddress'] ||
        errors['invalidZip'] ||
        errors['zipStateMismatch'] ||
        errors['incompleteAddress'] ||
        errors['invalidState'] ||
        errors['invalidCity']
      ) {
        return true;
      }
    }

    // Check control-level errors for state and city
    const stateControl = this.shippingForm.get('state');
    const cityControl = this.shippingForm.get('city');
    const zipControl = this.shippingForm.get('zip');

    if (stateControl?.errors?.['invalidState']) {
      return true;
    }
    if (cityControl?.errors?.['invalidCity']) {
      return true;
    }
    if (zipControl?.errors?.['invalidZip']) {
      return true;
    }

    return false;
  }

  /**
   * Clears address validation errors when the user starts typing in any address field.
   * This allows the continue button to be re-enabled.
   */
  protected clearAddressErrorsOnUserInput(): void {
    if (this.hasAddressError) {
      this.shippingForm.setErrors(null);
      this.cdr.markForCheck();
    }
  }

  /**
   * Provides user feedback when the Continue action cannot proceed because of
   * form validation errors. Shows a toast so the user always sees something
   * (useful when the cart slide-out is closing or the address alert is
   * off-screen) and, on small screens, scrolls the form back to the top so
   * the "Review your address" alert is visible.
   */
  private notifyValidationIssue(): void {
    this.toast.critical('Please review your address before continuing.');
    if (this.smallScreen) {
      setTimeout(() => {
        this.formScrollAnchor?.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    }
  }

  private handleValidationResult(errors: ValidationErrors | null): void {
    const verifiedAddressRequired = Boolean(
      errors?.['suggestedAddress'] &&
      (errors['invalidCity'] || errors['invalidZip']),
    );
    const hasOptionalSuggestion = Boolean(
      errors?.['suggestedAddress'] && errors['needsSuggestion'],
    );
    this.store.dispatch(
      addressActions.setVerifiedAddressRequired({
        required: verifiedAddressRequired,
      }),
    );

    if (errors && !verifiedAddressRequired && !hasOptionalSuggestion) {
      this.shippingForm.setErrors(errors);
      this.cdr.markForCheck();
      this.notifyValidationIssue();
    } else {
      const originalAddress = this.originalAddress;
      this.store.dispatch(
        addressActions.saveValidatedAddress({
          address: originalAddress,
        }),
      );

      this.router.navigate(['../suggested-address'], {
        relativeTo: this.route,
      });
    }
  }
}
